/**
 * SearchInput.tsx — 带搜索图标 + 清除按钮的输入框（移植自原型 searchinput.jsx）
 */

import { type CSSProperties, type InputHTMLAttributes, useRef, useState } from 'react';
import { Icons } from '~/lib/icons';

interface SearchChangeEvent {
  target: { value: string; name?: string };
}

interface SearchInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'style' | 'size'> {
  value?: string;
  onChange?: (e: SearchChangeEvent) => void;
  placeholder?: string;
  autoFocus?: boolean;
  width?: number | string;
  style?: CSSProperties;
  size?: 'sm';
  className?: string;
  iconLeft?: boolean;
  onClear?: () => void;
}

export function SearchInput({
  value,
  onChange,
  placeholder = '搜索…',
  autoFocus,
  width,
  style,
  size,
  className = 'input',
  iconLeft = true,
  onClear,
  name,
  onKeyDown,
  ...rest
}: SearchInputProps) {
  const controlled = value !== undefined;
  const [internal, setInternal] = useState('');
  const current = controlled ? (value ?? '') : internal;
  const ref = useRef<HTMLInputElement>(null);

  const set = (v: string) => {
    if (!controlled) setInternal(v);
    onChange?.({ target: { value: v, name } });
  };
  const clear = () => {
    set('');
    onClear?.();
    ref.current?.focus();
  };

  const heightSm = size === 'sm';

  return (
    <div
      style={{ position: 'relative', width: width || '100%', display: 'inline-block', ...style }}
    >
      {iconLeft && (
        <Icons.IconSearch
          size={13}
          style={{
            position: 'absolute',
            left: 10,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--fg-muted)',
            pointerEvents: 'none',
            display: 'block',
          }}
        />
      )}
      <input
        ref={ref}
        name={name}
        className={className}
        value={current}
        onChange={(e) => set(e.target.value)}
        placeholder={placeholder}
        style={{
          paddingLeft: iconLeft ? 30 : undefined,
          paddingRight: current ? 28 : 10,
          width: '100%',
          ...(heightSm ? { height: 26, fontSize: 12 } : {}),
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && current) {
            e.preventDefault();
            clear();
          }
          onKeyDown?.(e);
        }}
        {...rest}
      />
      {current && (
        <button
          type="button"
          onClick={clear}
          title="清除 (Esc)"
          style={{
            position: 'absolute',
            right: 6,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 18,
            height: 18,
            padding: 0,
            border: 0,
            background: 'var(--bg-muted)',
            borderRadius: 999,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--fg-muted)',
            transition: 'background 0.1s, color 0.1s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--border-strong)';
            e.currentTarget.style.color = 'var(--fg)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'var(--bg-muted)';
            e.currentTarget.style.color = 'var(--fg-muted)';
          }}
        >
          <Icons.IconX size={10} />
        </button>
      )}
    </div>
  );
}
